import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiGet, apiPost, apiPut, apiDelete } from "../../services/api";
import type { Location } from "../../types";

const DEFAULT_BUILDING_TYPES = [
  { id: 1, label: "Office" },
  { id: 2, label: "Warehouse" },
  { id: 3, label: "Store" },
  { id: 4, label: "Distribution Center" },
  { id: 5, label: "Restaurant" },
  { id: 6, label: "Service Center" },
  { id: 7, label: "Manufacturing" },
  { id: 8, label: "Showroom" },
  { id: 9, label: "Other" },
];

const EMPTY_FORM = {
  name: "",
  buildingType: 3,
  phone: "",
  email: "",
  address: { street: "", street2: "", city: "", state: "", zip: "", country: "US" },
};

export default function LocationsTab() {
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const [locations, setLocations] = useState<Location[]>([]);
  const [buildingTypes, setBuildingTypes] = useState(DEFAULT_BUILDING_TYPES);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => { if (orgId) loadData(); }, [orgId]);

  async function loadData() {
    try {
      const [locs, config] = await Promise.all([
        apiGet<Location[]>(`/organizations/${orgId}/locations`),
        apiGet<{ buildingTypes?: { id: number; label: string }[] }>(`/organizations/${orgId}/config`),
      ]);
      setLocations(locs);
      setBuildingTypes([...DEFAULT_BUILDING_TYPES, ...(config.buildingTypes || [])].sort((a, b) => a.id - b.id));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setShowModal(true); };
  const openEdit = (loc: any) => {
    setEditing(loc.locationId);
    setForm({ name: loc.name || "", buildingType: loc.buildingType || 3, phone: loc.phone || "", email: loc.email || "", address: loc.address || EMPTY_FORM.address });
    setShowModal(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name.startsWith("address.")) {
      const field = name.replace("address.", "");
      setForm((prev) => ({ ...prev, address: { ...prev.address, [field]: value } }));
    } else if (name === "buildingType") {
      setForm((prev) => ({ ...prev, buildingType: parseInt(value) }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editing) {
      await apiPut(`/organizations/${orgId}/locations/${editing}`, form);
    } else {
      await apiPost(`/organizations/${orgId}/locations`, form);
    }
    setShowModal(false);
    await loadData();
  };

  const handleDelete = async (locationId: string) => {
    if (!confirm("Delete this location?")) return;
    await apiDelete(`/organizations/${orgId}/locations/${locationId}`);
    await loadData();
  };

  const getBuildingLabel = (type: number) => buildingTypes.find((b) => b.id === type)?.label || `Type ${type}`;

  if (loading) return <div className="empty-state">Loading locations...</div>;

  return (
    <div>
      <div className="org-mgmt-header">
        <h2>Locations</h2>
        <button className="btn btn-primary" style={{ width: "auto" }} onClick={openCreate}>+ Add Location</button>
      </div>

      {locations.length === 0 ? (
        <div className="empty-state"><p>No locations yet.</p></div>
      ) : (
        <table className="data-table">
          <thead><tr><th>Store #</th><th>Name</th><th>Type</th><th>City</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {locations.map((loc: any) => (
              <tr key={loc.locationId}>
                <td><code style={{ fontSize: "0.85rem", background: "#f3f4f6", padding: "0.2rem 0.5rem", borderRadius: "4px" }}>{loc.storeNumber}</code></td>
                <td style={{ fontWeight: 500 }}>{loc.name}</td>
                <td>{getBuildingLabel(loc.buildingType)}</td>
                <td>{loc.address?.city || "—"}, {loc.address?.state || ""}</td>
                <td><span className={`status-badge ${loc.status}`}>{loc.status}</span></td>
                <td><div className="table-actions"><button onClick={() => openEdit(loc)}>Edit</button><button className="delete" onClick={() => handleDelete(loc.locationId)}>Delete</button></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editing ? "Edit Location" : "Add Location"}</h3>
            <form onSubmit={handleSubmit}>
              <div className="settings-form">
                <div className="form-row">
                  <div className="form-group"><label>Name / Alias</label><input name="name" value={form.name} onChange={handleChange} required /></div>
                  <div className="form-group"><label>Building Type</label><select name="buildingType" value={form.buildingType} onChange={handleChange}>{buildingTypes.map((bt) => (<option key={bt.id} value={bt.id}>{bt.id} — {bt.label}</option>))}</select></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Phone</label><input name="phone" value={form.phone} onChange={handleChange} /></div>
                  <div className="form-group"><label>Email</label><input name="email" type="email" value={form.email} onChange={handleChange} /></div>
                </div>
                <div className="form-group"><label>Street</label><input name="address.street" value={form.address.street} onChange={handleChange} required /></div>
                <div className="form-group"><label>Suite (optional)</label><input name="address.street2" value={form.address.street2} onChange={handleChange} /></div>
                <div className="form-row-3">
                  <div className="form-group"><label>City</label><input name="address.city" value={form.address.city} onChange={handleChange} required /></div>
                  <div className="form-group"><label>State</label><input name="address.state" value={form.address.state} onChange={handleChange} required /></div>
                  <div className="form-group"><label>ZIP</label><input name="address.zip" value={form.address.zip} onChange={handleChange} required /></div>
                </div>
                <div className="form-group"><label>Country</label><select name="address.country" value={form.address.country} onChange={handleChange}><option value="US">United States</option><option value="CA">Canada</option><option value="GB">United Kingdom</option><option value="MX">Mexico</option></select></div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ width: "auto" }}>{editing ? "Save Changes" : "Create Location"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
